/**
 * ✈️ AIRPORT MANAGEMENT PAGE
 * Admin interface for managing airports and their service zones
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Flight,
  Room,
  AccessTime,
  DirectionsCar,
  Public,
  ZoomIn,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { airportService, Airport, CreateAirportData, UpdateAirportData } from '@/services/airportService';

export const AirportManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState<{ open: boolean; airport: Airport | null }>({
    open: false,
    airport: null
  });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; airport: Airport | null }>({
    open: false,
    airport: null
  });
  const [formData, setFormData] = useState<CreateAirportData>({
    airportCode: '',
    airportName: '',
    city: '',
    state: '',
    country: '',
    latitude: 0,
    longitude: 0,
    timezone: 'Africa/Lagos'
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadAirports();
  }, []);

  const loadAirports = async () => {
    setLoading(true);
    try {
      const response = await airportService.getAirports();
      if (response.success && response.airports) {
        // Ensure we always set an array
        setAirports(Array.isArray(response.airports) ? response.airports : []);
      } else {
        setAirports([]); // Reset to empty array on error
        toast.error(response.error || 'Failed to load airports');
      }
    } catch (error) {
      console.error('Error loading airports:', error);
      setAirports([]); // Reset to empty array on error
      toast.error('Failed to load airports');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      airportCode: '',
      airportName: '',
      city: '',
      state: '',
      country: 'Nigeria',
      latitude: 0,
      longitude: 0,
      timezone: 'Africa/Lagos'
    });
    setCreateDialog(true);
  };

  const handleEdit = (airport: Airport) => {
    setFormData({
      airportCode: airport.airportCode,
      airportName: airport.airportName,
      city: airport.city,
      state: airport.state,
      country: airport.country,
      latitude: airport.coordinates?.latitude || 0,
      longitude: airport.coordinates?.longitude || 0,
      timezone: airport.timezone
    });
    setEditDialog({ open: true, airport });
  };

  const handleSubmitCreate = async () => {
    if (!formData.airportCode || !formData.airportName || !formData.city) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await airportService.createAirport(formData);
      if (response.success) {
        toast.success('Airport created successfully');
        setCreateDialog(false);
        await loadAirports();
      } else {
        toast.error(response.error || 'Failed to create airport');
      }
    } catch (error) {
      console.error('Error creating airport:', error);
      toast.error('Failed to create airport');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async () => {
    if (!editDialog.airport) return;

    if (!formData.airportCode || !formData.airportName || !formData.city) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const updateData: UpdateAirportData = {
        airportCode: formData.airportCode,
        airportName: formData.airportName,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        latitude: formData.latitude,
        longitude: formData.longitude,
        timezone: formData.timezone
      };

      const response = await airportService.updateAirport(editDialog.airport.id, updateData);
      if (response.success) {
        toast.success('Airport updated successfully');
        setEditDialog({ open: false, airport: null });
        await loadAirports();
      } else {
        toast.error(response.error || 'Failed to update airport');
      }
    } catch (error) {
      console.error('Error updating airport:', error);
      toast.error('Failed to update airport');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.airport) return;

    setSubmitting(true);
    try {
      const response = await airportService.deleteAirport(deleteDialog.airport.id);
      if (response.success) {
        toast.success('Airport deleted successfully');
        setDeleteDialog({ open: false, airport: null });
        await loadAirports();
      } else {
        toast.error(response.error || 'Failed to delete airport');
      }
    } catch (error) {
      console.error('Error deleting airport:', error);
      toast.error('Failed to delete airport');
    } finally {
      setSubmitting(false);
    }
  };

  const handleManageZones = (airport: Airport) => {
    navigate(`/service-areas/airports/${airport.id}/zones`);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Flight /> Airport Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage airports and their service zones for airport transfer trips
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
        >
          Add Airport
        </Button>
      </Box>

      {/* Stats Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Airports
              </Typography>
              <Typography variant="h4">
                {airports.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Active Airports
              </Typography>
              <Typography variant="h4">
                {airports.filter(a => a.isActive).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Zones
              </Typography>
              <Typography variant="h4">
                {airports.reduce((sum, a) => sum + (a.stats?.zoneCount || 0), 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Airports Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : airports.length === 0 ? (
            <Alert severity="info">
              No airports found. Click "Add Airport" to create your first airport.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Airport Name</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Coordinates</TableCell>
                    <TableCell>Timezone</TableCell>
                    <TableCell align="center">Zones</TableCell>
                    <TableCell align="center">Listings</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {airports.map((airport) => (
                    <TableRow key={airport.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {airport.airportCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack>
                          <Typography variant="body2" fontWeight={500}>
                            {airport.airportName}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">
                            {airport.city}, {airport.state}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            <Public fontSize="inherit" /> {airport.country}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {airport.coordinates ? (
                          <Tooltip title="View on map">
                            <Stack spacing={0.5}>
                              <Typography variant="caption">
                                <Room fontSize="inherit" /> {airport.coordinates.latitude.toFixed(4)}, {airport.coordinates.longitude.toFixed(4)}
                              </Typography>
                            </Stack>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            No coordinates
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTime fontSize="inherit" /> {airport.timezone}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={airport.stats?.zoneCount || 0}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={airport.stats?.activeListingsCount || 0}
                          size="small"
                          color="success"
                          variant="outlined"
                          icon={<DirectionsCar />}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={airport.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={airport.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Manage Zones">
                            <IconButton
                              size="small"
                              onClick={() => handleManageZones(airport)}
                              color="primary"
                            >
                              <ZoomIn fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Airport">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(airport)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Airport">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteDialog({ open: true, airport })}
                              color="error"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialog} onClose={() => !submitting && setCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Airport</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Airport Code"
                value={formData.airportCode}
                onChange={(e) => setFormData({ ...formData, airportCode: e.target.value.toUpperCase() })}
                fullWidth
                required
                placeholder="LOS"
                helperText="IATA code (e.g., LOS, ABV)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Airport Name"
                value={formData.airportName}
                onChange={(e) => setFormData({ ...formData, airportName: e.target.value })}
                fullWidth
                required
                placeholder="Murtala Muhammed International Airport"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                fullWidth
                required
                placeholder="Lagos"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="State"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                fullWidth
                required
                placeholder="Lagos"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                fullWidth
                required
                placeholder="Nigeria"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Timezone"
                value={formData.timezone}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                fullWidth
                required
                placeholder="Africa/Lagos"
                helperText="IANA timezone identifier"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Latitude"
                type="number"
                value={formData.latitude}
                onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) })}
                fullWidth
                placeholder="6.5774"
                inputProps={{ step: '0.0001' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Longitude"
                type="number"
                value={formData.longitude}
                onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) })}
                fullWidth
                placeholder="3.3213"
                inputProps={{ step: '0.0001' }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitCreate}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <Add />}
          >
            {submitting ? 'Creating...' : 'Create Airport'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => !submitting && setEditDialog({ open: false, airport: null })} maxWidth="md" fullWidth>
        <DialogTitle>Edit Airport</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Airport Code"
                value={formData.airportCode}
                onChange={(e) => setFormData({ ...formData, airportCode: e.target.value.toUpperCase() })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Airport Name"
                value={formData.airportName}
                onChange={(e) => setFormData({ ...formData, airportName: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="State"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Timezone"
                value={formData.timezone}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Latitude"
                type="number"
                value={formData.latitude}
                onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) })}
                fullWidth
                inputProps={{ step: '0.0001' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Longitude"
                type="number"
                value={formData.longitude}
                onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) })}
                fullWidth
                inputProps={{ step: '0.0001' }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, airport: null })} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitEdit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <Edit />}
          >
            {submitting ? 'Updating...' : 'Update Airport'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => !submitting && setDeleteDialog({ open: false, airport: null })} maxWidth="sm">
        <DialogTitle>Delete Airport?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete <strong>{deleteDialog.airport?.airportName}</strong>?
          </Alert>
          {deleteDialog.airport && deleteDialog.airport.stats && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                This airport has:
              </Typography>
              <ul>
                <li><Typography variant="body2">{deleteDialog.airport.stats.zoneCount} zones</Typography></li>
                <li><Typography variant="body2">{deleteDialog.airport.stats.activeListingsCount} active listings</Typography></li>
              </ul>
              {deleteDialog.airport.stats.activeListingsCount > 0 && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  This airport has active listings. Deleting it may affect ongoing bookings!
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, airport: null })} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <Delete />}
          >
            {submitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AirportManagementPage;
